import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import TagInput from '../TagInput/TagInput';

/**
 * `SidebarTab` is a child container for the {@link Sidebar} component.
 *
 * The container consists of an optional {@link TagInput} search field at the top and the rest of the space taken up by the
 * components you add to the sidebar.
 *
 * All props except *className*, *icon* and *iconName* will be passed on to the {@link TagInput} search field. See
 * {@link TagInput} and {@link Input} for supported props.
 *
 * If you need a custom search field, just set *showSearch* to `false` and add your own field as child. You may have to add
 * your own styling to make it consistent with the standard search field.
 *
 * @property {node} children The components you want to add to the sidebar.
 * @property {string} className The class name of your sidebar tab. Will be appended to "core-sidebar-content".
 * @property {node} icon An optional SVG icon to use as tab icon. Defaults to no icon.
 * @property {string} iconName The name of any icon in the **vimond-core-component**'s icon library. See {@link Icon}.
 * @property {boolean} showSearch If set to `true`, the {@link TagInput} component will be shown. Defaults to `true`. Set
 * this to `false` if you want to add your own search field.
 * @property {function} tagRenderer The tag renderer callback that will be called for all tags in *value*. Tags will be
 * rendered in the default way if no *tagRenderer* is set.
 * @property {string|number} value The search input value.
 *
 * @see Icon, Input, Sidebar, TagInput
 */
export default class SidebarTab extends Component {

  static displayName = 'SidebarTab';

  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    icon: PropTypes.node,
    iconName: PropTypes.string,
    placeholder: PropTypes.string,
    showSearch: PropTypes.bool,
    tagRenderer: PropTypes.func,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  };

  static defaultProps = {
    placeholder: 'Search',
    showSearch: true,
  };

  render() {
    const { children, className, icon, iconName, showSearch, ...rest } = this.props; // eslint-disable-line no-unused-vars

    return (
      <div className={cx('core-sidebar-content', className)}>
        {showSearch && <TagInput className="core-sidebar-search" {...rest} showUnderline={false} showSearch/>}
        {children}
      </div>
    );
  }

}
